import { CURRENT_POSITION, CYCLE_SEGMENTS } from "@/lib/cycleData";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";

const CX = 100;
const CY = 104;
const R = 78;

function polar(fraction: number) {
  const angle = Math.PI * (1 - fraction);
  return { x: CX + R * Math.cos(angle), y: CY - R * Math.sin(angle) };
}

function arcPath(from: number, to: number) {
  const a = polar(from);
  const b = polar(to);
  return `M ${a.x.toFixed(2)} ${a.y.toFixed(2)} A ${R} ${R} 0 0 1 ${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
}

interface CycleGaugeProps {
  /** Rendered max width in px. */
  size?: number;
  className?: string;
}

export function CycleGauge({ size = 320, className }: CycleGaugeProps) {
  const { ref, inView } = useInView<HTMLDivElement>(0.2);

  let cursor = 0;
  const segments = CYCLE_SEGMENTS.map((seg) => {
    const start = cursor;
    cursor += seg.fraction;
    return { ...seg, start, end: cursor, mid: (start + cursor) / 2 };
  });

  const dot = polar(CURRENT_POSITION);

  return (
    <div
      ref={ref}
      className={cn("w-full mx-auto", className)}
      style={{ maxWidth: size }}
      role="img"
      aria-label="Seven-year cycle gauge: currently year 4, Build phase"
    >
      <svg viewBox="-48 0 296 132" width="100%" className="overflow-visible">
        {/* track */}
        <path
          d={arcPath(0, 1)}
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth="10"
          strokeLinecap="round"
          opacity={0.5}
        />

        {segments.map((seg, i) => (
          <path
            key={seg.key}
            d={arcPath(seg.start + 0.004, seg.end - 0.004)}
            fill="none"
            stroke={seg.color}
            strokeWidth="10"
            strokeLinecap="butt"
            pathLength={100}
            className={inView ? "cycle-arc-draw" : undefined}
            style={{
              strokeDasharray: 100,
              strokeDashoffset: inView ? undefined : 100,
              animationDelay: `${i * 0.12}s`,
            }}
          />
        ))}

        {/* phase labels outside the arc */}
        {segments.map((seg) => {
          const angle = Math.PI * (1 - seg.mid);
          const cos = Math.cos(angle);
          const lr = R + 12;
          const x = CX + lr * cos;
          const y = CY - lr * Math.sin(angle);
          const anchor = cos < -0.2 ? "end" : cos > 0.2 ? "start" : "middle";
          return (
            <text
              key={seg.key}
              x={x}
              y={y}
              fill={seg.color}
              fontSize="9"
              textAnchor={anchor}
              dominantBaseline="middle"
              className="font-plex"
              style={{ letterSpacing: "0.06em" }}
            >
              {seg.label}
            </text>
          );
        })}


        {/* position dot */}
        <g
          className={cn("cycle-dot", inView && "cycle-dot-in")}
          style={{ opacity: inView ? undefined : 0 }}
        >
          <circle cx={dot.x} cy={dot.y} r="8" fill="var(--cycle-teal-hex)" opacity="0.28" className="cycle-dot-pulse" />
          <circle
            cx={dot.x}
            cy={dot.y}
            r="5.5"
            fill="var(--cycle-teal-hex)"
            stroke="hsl(var(--foreground))"
            strokeWidth="3"
            paintOrder="stroke"
          />
        </g>

        {/* centre readout */}
        <text
          x={CX}
          y={CY - 26}
          textAnchor="middle"
          className="font-plex"
          fontSize="32"
          fontWeight="700"
          fill="hsl(var(--foreground))"
        >
          Y4
        </text>
        <text x={CX} y={CY - 10} textAnchor="middle" fontSize="10" fill="hsl(var(--muted-foreground))">
          of 7-year cycle
        </text>
        <text
          x={CX}
          y={CY + 4}
          textAnchor="middle"
          className="font-plex"
          fontSize="9.5"
          fill="var(--cycle-teal-hex)"
        >
          Build phase
        </text>

        {/* year anchors */}
        <text x={CX} y={12} textAnchor="middle" className="font-plex" fontSize="9" fill="hsl(var(--muted-foreground))">
          2025
        </text>
        <text x={CX - R} y={CY + 16} textAnchor="middle" className="font-plex" fontSize="9" fill="hsl(var(--muted-foreground))">
          2022
        </text>
        <text x={CX + R} y={CY + 16} textAnchor="middle" className="font-plex" fontSize="9" fill="hsl(var(--muted-foreground))">
          2029
        </text>
      </svg>
    </div>
  );
}

export default CycleGauge;
